/**
 * ===========================================================================
 * WHAT IS WAITING FOR ME — the five states a badge is allowed to be in
 * ===========================================================================
 *
 * A badge is a number, and a number is the easiest place in a product to
 * collapse the four states this repository keeps having to separate. All four
 * of these end up rendering as an absent badge, or as a quiet zero, unless
 * something makes them different:
 *
 *   "you have no unread mentions"        a real, measured zero
 *   "we could not check"                 a read that did not come back
 *   "you may not see mentions"           refused for scope
 *   "we have not looked yet"             nothing has been asked
 *
 * Lane DOCCOMP found `counts.pastDue: null` rendered as a permanent `0` and had
 * to make absence its own paint. A badge is smaller than a table cell, so the
 * same trap is easier to fall into and harder to notice.
 *
 * `apps/worker-mobile/src/lib/unread-messages.ts` already carries half of this
 * for one signal — `number | null`, with a docblock explaining that null and 0
 * must stay distinguishable in the model even where they look identical on
 * screen. Two states was enough while the only consumer was a dot. It is not
 * enough for a surface that puts the number into WORDS, which is exactly where
 * the admin mentions line was wrong on trunk:
 *
 *     const totalMentions = myMentions.data?.length ?? 0;   // Messages.tsx
 *
 * `data` is `undefined` while loading, `undefined` after a 403, and `[]` for a
 * real zero, and all three became `0`; a full page of 50 became the sentence
 * "50 mentions of you" for somebody who had five hundred.
 *
 * ---------------------------------------------------------------------------
 * THE FLOOR
 * ---------------------------------------------------------------------------
 *
 * The founder's attention ruling of 2026-09-08: attention is budgeted,
 * obligations are not discarded. Deduplicate, aggregate, rank, defer, respect
 * quiet hours — but never erase a still-actionable obligation because a quota
 * was reached. A count that is capped, or that silently stops counting, is that
 * ruling's shape in miniature. So when the server says the page was full, the
 * surface says the number is a FLOOR rather than a total. Same discipline, same
 * wording, as `intelligenceMemory.holdingSentence`'s "At least N ... (this page
 * is full, so there may be more)".
 *
 * ---------------------------------------------------------------------------
 * WHY ONE MODULE FOR BOTH APPS AND BOTH SIGNALS
 * ---------------------------------------------------------------------------
 *
 * Unread mentions and unread notifications are the same question asked twice —
 * what is waiting for me, and how do I know without opening everything. Four
 * surfaces render an answer to it (admin Messages, worker Messages, worker
 * More, worker Notifications) and a fifth will. `sentenceOf` is ONE function
 * for the same reason `holdingSentence` is: five states cannot be worded into
 * each other by a screen that only remembered three of them.
 */
export const UNASKED = { kind: 'unasked' };
export const UNAVAILABLE = { kind: 'unavailable' };
export const REFUSED = { kind: 'refused' };
export const NONE = { kind: 'none' };
/**
 * An answered read of a PAGE of obligations.
 *
 * `windowExhausted` comes from the server — the client cannot derive it, because
 * a page of 50 out of exactly 50 and a page of 50 out of 500 look identical from
 * here. An exhausted page with zero rows is impossible (the limit is at least
 * 1), so `none` never carries a floor.
 */
export function fromPage(rows, windowExhausted) {
    if (rows.length === 0)
        return NONE;
    return { kind: 'some', count: rows.length, isFloor: windowExhausted };
}
/**
 * An answered read of an EXACT count — a `count(*)` the server computed over
 * the whole scope, with no page in the way. Never a floor.
 *
 * A negative count is not a count; it is treated as an unavailable answer rather
 * than clamped to zero, because clamping would manufacture the very "you have
 * nothing" statement this module exists to withhold.
 */
export function fromExactCount(count) {
    if (!Number.isFinite(count) || count < 0)
        return UNAVAILABLE;
    if (count === 0)
        return NONE;
    return { kind: 'some', count, isFloor: false };
}
/**
 * A read that threw.
 *
 * `isRefusal` is passed in rather than sniffed here: the two apps classify HTTP
 * failures with their own helpers (`apps/worker-mobile/src/lib/api-error.ts`,
 * and the admin's query error shape), and re-implementing that classification in
 * a third place is how the two drift apart.
 */
export function fromFailure(isRefusal) {
    return isRefusal ? REFUSED : UNAVAILABLE;
}
/**
 * Does this state light a badge?
 *
 * ONLY `some`. A badge is a PROMPT, and the honest default for a fact we do not
 * hold is not to prompt — the same rule `unread-messages.ts` states for its
 * `null`. The three unknown-ish states therefore look identical HERE and stay
 * distinguishable in the model, which is what `sentenceOf` needs.
 */
export function badges(attention) {
    return attention.kind === 'some';
}
/**
 * The number to paint in a badge, or `null` for every state that has none.
 *
 * Returns the count for `some` and null otherwise — INCLUDING for `none`. A "0"
 * painted in a badge is noise at best; the absence of the badge is the zero.
 */
export function badgeCount(attention) {
    return attention.kind === 'some' ? attention.count : null;
}
/**
 * The badge's own text, floor included. `47` or `47+`, never a bare `47` for a
 * capped page.
 *
 * The `+` is the smallest honest carrier of the floor in a space too small for
 * a sentence, and it is a shape people already read as "at least this many".
 */
export function badgeLabel(attention) {
    if (attention.kind !== 'some')
        return null;
    return attention.isFloor ? `${attention.count}+` : `${attention.count}`;
}
/**
 * The sentence. ONE function, so five states cannot be worded into each other.
 *
 * Every branch says what is true and, where the truth is that we do not know,
 * says THAT rather than a number. Note that `unasked`, `unavailable` and
 * `refused` produce three DIFFERENT sentences: "we have not looked", "we could
 * not check" and "you may not see this" are three different things to be told,
 * and a person who is told the wrong one takes the wrong next action.
 */
export function sentenceOf(attention, noun) {
    switch (attention.kind) {
        case 'unasked':
            return `Checking for ${noun.many}…`;
        case 'unavailable':
            return `We could not check your ${noun.many} just now. This is not the same as having none.`;
        case 'refused':
            return `Your account cannot see ${noun.many}.`;
        case 'none':
            return `No unread ${noun.many}.`;
        case 'some':
            return attention.isFloor
                ? `At least ${attention.count} unread ${noun.many} (this page is full, so there may be more).`
                : `${attention.count} unread ${attention.count === 1 ? noun.one : noun.many}.`;
    }
}
/**
 * The accessible label for a badge — what a screen reader announces.
 *
 * Separate from `sentenceOf` because a badge's label is read in the context of
 * the control it sits on ("Notifications, 3 unread") and must stay short, and
 * because the three unknown states have no badge to label at all.
 */
export function badgeAccessibilityLabel(attention, noun) {
    if (attention.kind !== 'some')
        return null;
    const unit = attention.count === 1 ? noun.one : noun.many;
    return attention.isFloor
        ? `At least ${attention.count} unread ${unit}`
        : `${attention.count} unread ${unit}`;
}
export const MENTIONS = { one: 'mention of you', many: 'mentions of you' };
export const NOTIFICATIONS = { one: 'notification', many: 'notifications' };
//# sourceMappingURL=attention.js.map